import { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const SizingContext = createContext(null);

const uid = (prefix) =>
    `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export function SizingProvider({ children }) {
    const { session } = useAuth();
    const [state, setState] = useState({
        series: [], groups: [], models: [], tables: [], products: [], links: {},
        fitLabels: null, images: [], productsTotal: 0, productsVersion: 0, loaded: false,
    });
    // Product lookup subscribers get bumped whenever the products cache changes.
    // The paginated Products page listens for this to keep its view fresh when
    // a mutation happens elsewhere (e.g. Models dialog reassigns a product).
    const stateRef = useRef(state);
    stateRef.current = state;

    // Bump the products version — signal to `useProductsQuery` consumers
    // that a product mutation happened somewhere. Wrapped as a helper so
    // action handlers can call it explicitly (only mutations that touch
    // the visible product fields should bump it — series/group edits do not).
    const bumpProductsVersion = useCallback(() => {
        setState((p) => ({ ...p, productsVersion: (p.productsVersion || 0) + 1 }));
    }, []);

    const refresh = useCallback(async () => {
        try {
            // Bootstrap comes in two parallel requests:
            //   1) /state → series, groups, models, tables, fitLabels, images, productsTotal
            //   2) /products/summary → compact product lookup index (id/name/image/
            //      category/tableId/modelId/wornSize/fit/sizingImageId/exempt)
            // /products/summary keeps the wire size ~10x smaller than the old
            // /state products payload for large catalogs, and the Katalog
            // > Ürünler page uses its OWN paginated fetch on top of this.
            const [stateRes, summaryRes] = await Promise.all([
                api.get(`/state`),
                api.get(`/products/summary`),
            ]);
            const products = summaryRes.data?.items || [];
            const links = {};
            for (const p of products) {
                if (p.tableId) links[p.id] = p.tableId;
            }
            setState({ ...stateRes.data, products, links, loaded: true });
        } catch (e) {
            setState((p) => ({ ...p, loaded: true }));
            throw e;
        }
    }, []);

    // Reload only the products lookup index (used after bulk mutations that
    // touch many rows — cheaper than /state, still authoritative).
    const refreshProducts = useCallback(async () => {
        try {
            const { data } = await api.get(`/products/summary`);
            const products = data?.items || [];
            const links = {};
            for (const p of products) {
                if (p.tableId) links[p.id] = p.tableId;
            }
            setState((prev) => ({ ...prev, products, links }));
            return products;
        } catch (e) {
            console.error("[sizing-store] refreshProducts failed:", e);
            throw e;
        }
    }, []);

    // Only fetch state once the user is authenticated. Reset on logout.
    useEffect(() => {
        if (session && session.user) {
            refresh().catch(() => toast.error("Veriler yüklenemedi — sayfayı yenilemeyi dene"));
        } else if (session === false) {
            setState({ series: [], groups: [], models: [], tables: [], products: [], links: {}, fitLabels: null, images: [], productsTotal: 0, loaded: true });
        }
    }, [session, refresh]);

    const actions = useMemo(() => {
        // Optimistic mutation: update UI immediately, sync API in background, reload on failure
        const sync = (promise) =>
            promise.catch(() => {
                toast.error("Kaydedilemedi — veriler sunucudan yenileniyor");
                refresh();
            });

        const addSeries = ({ name, sizes }) => {
            const next = { id: uid("sys"), name: name.trim(), sizes: sizes.filter(Boolean) };
            setState((p) => ({ ...p, series: [...p.series, next] }));
            sync(api.post(`/series`, next));
            return next;
        };
        const updateSeries = (id, patch) => {
            setState((p) => ({ ...p, series: p.series.map((s) => (s.id === id ? { ...s, ...patch } : s)) }));
            sync(api.put(`/series/${id}`, { patch }));
        };
        const removeSeries = async (id) => {
            // Backend enforces "no linked tables" — surface 409 to the caller.
            try {
                await api.delete(`/series/${id}`);
            } catch (e) {
                if (e?.response?.status === 409) throw e; // caller handles modal
                toast.error(e?.response?.data?.detail || "Beden serisi silinemedi");
                await refresh();
                throw e;
            }
            setState((p) => ({ ...p, series: p.series.filter((s) => s.id !== id) }));
        };

        // Rename a size across a series template AND every table sourced
        // from it. There is no "isolated rename" mode any more — templates
        // are the single source of truth, so tables always follow. This
        // eliminates the fork-and-strand UX friction (rename "S" → "32"
        // locally used to leave 72 tables un-editable in bulk).
        const renameSizeInSeries = async (seriesId, oldName, newName) => {
            // Optimistic template update
            setState((p) => ({
                ...p,
                series: p.series.map((s) => (s.id !== seriesId ? s : {
                    ...s, sizes: (s.sizes || []).map((sz) => (sz === oldName ? newName : sz)),
                })),
            }));
            let res;
            try {
                res = await api.post(`/series/${seriesId}/rename-size`, { oldName, newName });
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Yeniden adlandırma başarısız — veriler yenileniyor");
                await refresh();
                throw e;
            }
            // Reload authoritative state — table snapshots + values were touched.
            await refresh();
            return res.data;
        };

        // Add a size to a series template, optionally propagating to a subset
        // of linked tables (opt-in). `addToTables` is "all" | "none" | string[].
        const addSizeToSeries = async (seriesId, name, addToTables) => {
            let res;
            try {
                res = await api.post(`/series/${seriesId}/add-size`, { name, addToTables });
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Beden eklenemedi");
                throw e;
            }
            await refresh();
            return res.data;
        };

        // Remove a size from a series template. Backend returns 409 with
        // `blockers` (tables that have data in that size) when unsafe. The
        // caller is expected to handle 409 by opening the blocker list UI.
        const deleteSizeFromSeries = async (seriesId, name) => {
            let res;
            try {
                res = await api.post(`/series/${seriesId}/delete-size`, { name });
            } catch (e) {
                // Bubble the 409 payload up — caller shows the blocker list.
                throw e;
            }
            await refresh();
            return res.data;
        };

        // Duplicate a series with a new name — creates an independent fork.
        const cloneSeries = async (seriesId, name) => {
            let res;
            try {
                res = await api.post(`/series/${seriesId}/clone`, name ? { name } : {});
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Seri kopyalanamadı");
                throw e;
            }
            await refresh();
            return res.data;
        };

        const addGroup = ({ name, measures }) => {
            const next = {
                id: uid("grp"),
                name: name.trim(),
                measures: measures
                    .map((m) => {
                        if (typeof m === "string") return { name: m };
                        return {
                            name: m.name,
                            ...(m.kind ? { kind: m.kind } : {}),
                            ...(m.howToMeasure ? { howToMeasure: m.howToMeasure } : {}),
                        };
                    })
                    .filter((m) => m.name),
            };
            setState((p) => ({ ...p, groups: [...p.groups, next] }));
            sync(api.post(`/groups`, next));
            return next;
        };
        const updateGroup = (id, patch) => {
            setState((p) => ({ ...p, groups: p.groups.map((g) => (g.id === id ? { ...g, ...patch } : g)) }));
            sync(api.put(`/groups/${id}`, { patch }));
        };
        const removeGroup = async (id) => {
            // Backend enforces "no linked tables" — we surface 409 to the
            // caller so the UI can list blocker tables.
            try {
                await api.delete(`/groups/${id}`);
            } catch (e) {
                if (e?.response?.status === 409) {
                    // Do NOT mutate state; caller handles the modal.
                    throw e;
                }
                toast.error(e?.response?.data?.detail || "Ölçü grubu silinemedi");
                await refresh();
                throw e;
            }
            setState((p) => ({ ...p, groups: p.groups.filter((g) => g.id !== id) }));
        };
        // Rename a measure across a group template AND every table sourced
        // from it — same "single source of truth" contract as sizes.
        const renameMeasureInGroup = async (groupId, oldName, newName) => {
            setState((p) => ({
                ...p,
                groups: p.groups.map((g) => (g.id !== groupId ? g : {
                    ...g,
                    measures: (g.measures || []).map((m) => (
                        (m.name || m) === oldName ? { ...(typeof m === "object" ? m : {}), name: newName } : m
                    )),
                })),
            }));
            let res;
            try {
                res = await api.post(`/groups/${groupId}/rename-measure`, { oldName, newName });
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Yeniden adlandırma başarısız — veriler yenileniyor");
                await refresh();
                throw e;
            }
            await refresh();
            return res.data;
        };

        // Add a measure to a group template + optionally to selected tables.
        const addMeasureToGroup = async (groupId, name, addToTables) => {
            let res;
            try {
                res = await api.post(`/groups/${groupId}/add-measure`, { name, addToTables });
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Ölçü eklenemedi");
                throw e;
            }
            await refresh();
            return res.data;
        };

        // Remove a measure — server returns 409 with blockers when unsafe.
        const deleteMeasureFromGroup = async (groupId, name) => {
            let res;
            try {
                res = await api.post(`/groups/${groupId}/delete-measure`, { name });
            } catch (e) {
                throw e;
            }
            await refresh();
            return res.data;
        };

        const cloneGroup = async (groupId, name) => {
            let res;
            try {
                res = await api.post(`/groups/${groupId}/clone`, name ? { name } : {});
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Grup kopyalanamadı");
                throw e;
            }
            await refresh();
            return res.data;
        };

        const addModel = (data) => {
            const next = { id: uid("mdl"), products: [], ...data, name: data.name.trim() };
            setState((p) => ({ ...p, models: [...p.models, next] }));
            sync(api.post(`/models`, next));
            return next;
        };
        const updateModel = (id, patch) => {
            setState((p) => ({ ...p, models: p.models.map((m) => (m.id === id ? { ...m, ...patch } : m)) }));
            sync(api.put(`/models/${id}`, { patch }));
        };
        const removeModel = (id) => {
            // Backend cascades: any product with modelId=X gets its FK cleared.
            // Keep client state in sync so UI reflects immediately.
            setState((p) => ({
                ...p,
                models: p.models.filter((m) => m.id !== id),
                products: p.products.map((pr) => (pr.modelId === id ? { ...pr, modelId: null, wornSize: null } : pr)),
            }));
            sync(api.delete(`/models/${id}`));
        };
        // Product ↔ Model: single source of truth on product.
        // Set modelId=null (and/or wornSize=null) to clear the assignment.
        // Model.delete cascades on server side (backend clears products.modelId).
        const setProductModel = (productId, modelId, wornSize) => {
            const mid = modelId || null;
            const worn = mid ? (wornSize || null) : null;
            setState((p) => ({
                ...p,
                products: p.products.map((pr) => (pr.id === productId ? { ...pr, modelId: mid, wornSize: worn } : pr)),
                productsVersion: (p.productsVersion || 0) + 1,
            }));
            sync(api.put(`/products/${productId}/model`, { modelId: mid, wornSize: worn }));
        };

        // ---- Tables & product-table links (a product has at most ONE table) ----
        const addTable = (data) => {
            const next = {
                id: uid("tbl"),
                active: true,
                values: {},
                updated: new Date().toISOString(),
                ...data,
                name: data.name.trim(),
            };
            setState((p) => {
                // If the new table has an imageId, increment that image's usageCount optimistically
                const images = next.imageId
                    ? p.images.map((img) => (img.id === next.imageId ? { ...img, usageCount: (img.usageCount || 0) + 1 } : img))
                    : p.images;
                return { ...p, tables: [...p.tables, next], images };
            });
            const { updated, ...payload } = next;
            // Expose the persist promise so callers that need to sequence
            // dependent calls (e.g. linkProduct after table create) can await
            // the backend commit before firing follow-ups. Otherwise there is
            // a race where `linkProduct` hits the API before /tables POST
            // finishes, and the backend rejects with "Tablo bulunamadı".
            const persistPromise = api.post(`/tables`, payload).catch((e) => {
                toast.error("Kaydedilemedi — veriler sunucudan yenileniyor");
                refresh();
                throw e;
            });
            next.persistPromise = persistPromise;
            return next;
        };
        const updateTable = (id, patch) => {
            setState((p) => ({
                ...p,
                tables: p.tables.map((t) => (t.id === id ? { ...t, ...patch, updated: new Date().toISOString() } : t)),
            }));
            return api.put(`/tables/${id}`, { patch }).catch((e) => {
                toast.error("Kaydedilemedi — veriler sunucudan yenileniyor");
                refresh();
                throw e;
            });
        };
        // Toggle the widget kill-switch WITHOUT bumping `updated`. Sorting by
        // last-edited would otherwise jump the row to the top and confuse the
        // merchant into thinking a different table was affected.
        const setTableActive = (id, active) => {
            setState((p) => ({
                ...p,
                tables: p.tables.map((t) => (t.id === id ? { ...t, active } : t)),
            }));
            sync(api.put(`/tables/${id}`, { patch: { active }, touchUpdated: false }));
        };
        const removeTable = (id) => {
            setState((p) => ({
                ...p,
                tables: p.tables.filter((t) => t.id !== id),
                links: Object.fromEntries(Object.entries(p.links).filter(([, tid]) => tid !== id)),
                products: p.products.map((pr) => (pr.tableId === id ? { ...pr, tableId: null } : pr)),
            }));
            sync(api.delete(`/tables/${id}`));
        };
        const linkProduct = (productId, tableId) => {
            setState((p) => ({
                ...p,
                links: { ...p.links, [productId]: tableId },
                products: p.products.map((pr) => (pr.id === productId ? { ...pr, tableId } : pr)),
                productsVersion: (p.productsVersion || 0) + 1,
            }));
            sync(api.post(`/products/${productId}/link`, { tableId }));
        };
        const unlinkProduct = (productId) => {
            setState((p) => {
                const next = { ...p.links };
                delete next[productId];
                return {
                    ...p,
                    links: next,
                    products: p.products.map((pr) => (pr.id === productId ? { ...pr, tableId: null } : pr)),
                    productsVersion: (p.productsVersion || 0) + 1,
                };
            });
            sync(api.delete(`/products/${productId}/link`));
        };
        // Mark a product as "size table not applicable" (accessory, fragrance,
        // etc). Setting exempt=true also clears any active tableId link since
        // the two states are mutually exclusive server-side.
        const setProductSizingExempt = (productId, exempt) => {
            setState((p) => {
                const nextLinks = { ...p.links };
                if (exempt) delete nextLinks[productId];
                return {
                    ...p,
                    links: nextLinks,
                    products: p.products.map((pr) => (
                        pr.id === productId
                            ? { ...pr, sizingTableExempt: !!exempt, tableId: exempt ? null : pr.tableId }
                            : pr
                    )),
                    productsVersion: (p.productsVersion || 0) + 1,
                };
            });
            sync(api.put(`/products/${productId}/sizing-exempt`, { exempt: !!exempt }));
        };
        const setProductFit = (productId, fit) => {
            setState((p) => ({
                ...p,
                products: p.products.map((pr) => (pr.id === productId ? { ...pr, fit } : pr)),
                productsVersion: (p.productsVersion || 0) + 1,
            }));
            sync(api.put(`/products/${productId}/fit`, { fit }));
        };
        // Attach or clear the sizing-chart image for a single product. The
        // image comes from the tenant's Görseller (DAM) library.  Pass
        // `imageId=null` (or omit) to clear the assignment.
        //
        // Also keeps `images[].usageCount` in sync locally so the Images page
        // never lags behind: -1 on the previous image, +1 on the new one.
        // Same delta pattern as `setTableImage`.
        const setProductSizingImage = (productId, imageId) => {
            const nextId = imageId || null;
            setState((p) => {
                const prevProduct = p.products.find((pr) => pr.id === productId);
                const prevImageId = prevProduct?.sizingImageId || null;
                const newImages = (prevImageId === nextId) ? p.images : p.images.map((img) => {
                    let delta = 0;
                    if (img.id === prevImageId) delta -= 1;
                    if (img.id === nextId) delta += 1;
                    return delta ? { ...img, usageCount: Math.max(0, (img.usageCount || 0) + delta) } : img;
                });
                return {
                    ...p,
                    products: p.products.map((pr) =>
                        pr.id === productId ? { ...pr, sizingImageId: nextId } : pr
                    ),
                    images: newImages,
                    productsVersion: (p.productsVersion || 0) + 1,
                };
            });
            sync(api.put(`/products/${productId}/sizing-image`, { imageId: nextId }));
        };

        // ---- Product mapping CRUD (Ürün Eşleştirmeleri) ----
        // Bulk creation from a paste — server does canonical dedupe.
        const addProductsBulk = async ({ items, tableId }) => {
            const { data } = await api.post(`/products/bulk`, { items, tableId: tableId || null });
            // Pull just the compact products index — /state is unchanged.
            await refreshProducts();
            bumpProductsVersion();
            return data; // { created, updated, skipped }
        };

        // Scrape a customer product URL — does NOT persist, frontend confirms then bulk-adds.
        const scrapeProductUrl = async (url) => {
            const { data } = await api.post(`/products/from-url`, { url });
            return data;
        };

        // Batch URL scrape — returns per-URL outcomes, does NOT persist.
        const scrapeProductUrls = async (urls) => {
            const { data } = await api.post(`/products/from-urls`, { urls });
            return data; // { tenant_domain, results: [{url, sku?, name?, image?, ok, error?}] }
        };

        const removeProduct = (id) => {
            setState((p) => {
                const gone = p.products.find((pr) => pr.id === id);
                const goneImageId = gone?.sizingImageId || null;
                return {
                    ...p,
                    products: p.products.filter((pr) => pr.id !== id),
                    links: Object.fromEntries(Object.entries(p.links).filter(([pid]) => pid !== id)),
                    models: p.models.map((m) => ({
                        ...m,
                        products: (m.products || []).filter((x) => x.productId !== id),
                    })),
                    // Decrement usage on the image the deleted product referenced
                    // (if any) so the Images grid stays live-accurate.
                    images: goneImageId
                        ? p.images.map((img) => (img.id === goneImageId ? { ...img, usageCount: Math.max(0, (img.usageCount || 0) - 1) } : img))
                        : p.images,
                    productsTotal: Math.max(0, (p.productsTotal || 0) - 1),
                    productsVersion: (p.productsVersion || 0) + 1,
                };
            });
            sync(api.delete(`/products/${id}`));
        };

        const removeProductsBulk = async (ids) => {
            if (!ids?.length) return;
            setState((p) => {
                const idSet = new Set(ids);
                // Aggregate usage deltas per image so the Images grid reflects
                // the bulk cascade immediately (before any refresh).
                const deltas = {};
                for (const pr of p.products) {
                    if (idSet.has(pr.id) && pr.sizingImageId) {
                        deltas[pr.sizingImageId] = (deltas[pr.sizingImageId] || 0) + 1;
                    }
                }
                return {
                    ...p,
                    products: p.products.filter((pr) => !idSet.has(pr.id)),
                    links: Object.fromEntries(Object.entries(p.links).filter(([pid]) => !idSet.has(pid))),
                    models: p.models.map((m) => ({
                        ...m,
                        products: (m.products || []).filter((x) => !idSet.has(x.productId)),
                    })),
                    images: Object.keys(deltas).length
                        ? p.images.map((img) => (deltas[img.id] ? { ...img, usageCount: Math.max(0, (img.usageCount || 0) - deltas[img.id]) } : img))
                        : p.images,
                    productsTotal: Math.max(0, (p.productsTotal || 0) - idSet.size),
                    productsVersion: (p.productsVersion || 0) + 1,
                };
            });
            sync(api.post(`/products/bulk-delete`, { ids }));
        };

        const fetchUnknownSkus = async () => {
            const { data } = await api.get(`/products/unknown`);
            return data; // { items, count }
        };

        const dismissUnknownSkus = async (skus) => {
            await api.post(`/products/unknown/dismiss`, { skus });
        };

        // ---- Fit labels (3 locked presets) ----
        const updateFitLabels = async (labels) => {
            // Server validates all 3 types present + non-empty strings.
            const { data } = await api.put(`/fit-labels`, { labels });
            setState((p) => ({ ...p, fitLabels: data.labels }));
            return data.labels;
        };

        // ---- Images (DAM library) ----
        const uploadImage = async (file, { name, alt } = {}) => {
            const form = new FormData();
            form.append("file", file);
            if (name) form.append("name", name);
            if (alt) form.append("alt", alt);
            const { data } = await api.post(`/images`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setState((p) => ({ ...p, images: [data, ...p.images] }));
            return data;
        };
        const renameImage = async (id, patch) => {
            const { data } = await api.patch(`/images/${id}`, patch);
            setState((p) => ({
                ...p,
                images: p.images.map((img) => (img.id === id ? { ...img, ...data } : img)),
            }));
            return data;
        };
        // Upload a NEW VERSION of an existing image. All products referencing
        // `sizingImageId=<id>` instantly see the new bytes — no need to
        // reassign them.  The previous version(s) stay in `history` and can
        // be restored via `rollbackImageVersion`.
        const uploadImageVersion = async (id, file) => {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post(`/images/${id}/versions`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setState((p) => ({
                ...p,
                images: p.images.map((img) => (img.id === id ? { ...img, ...data } : img)),
            }));
            return data;
        };
        // Flip the current_version pointer back to an older entry.  Bytes
        // are not deleted, so the merchant can jump between versions freely.
        const rollbackImageVersion = async (id, version) => {
            const { data } = await api.post(`/images/${id}/rollback/${version}`);
            setState((p) => ({
                ...p,
                images: p.images.map((img) => (img.id === id ? { ...img, ...data } : img)),
            }));
            return data;
        };
        const removeImage = async (id) => {
            const { data } = await api.delete(`/images/${id}`);
            // Cascade unlink in local state — backend clears BOTH product
            // FKs (sizingImageId) AND legacy table imageIds, so we mirror
            // both to keep the UI consistent without a full refresh.
            setState((p) => ({
                ...p,
                images: p.images.filter((img) => img.id !== id),
                products: p.products.map((pr) => (pr.sizingImageId === id ? { ...pr, sizingImageId: null } : pr)),
                tables: p.tables.map((t) => (t.imageId === id ? { ...t, imageId: null, imagePosition: null } : t)),
            }));
            return data; // { ok, unlinkedProducts }
        };
        const setTableImage = (tableId, imageId, position) => {
            // Optimistic update: patch local tables[] + increment/decrement usage counts
            setState((p) => {
                const prevTable = p.tables.find((t) => t.id === tableId);
                const prevImageId = prevTable?.imageId || null;
                const newImages = p.images.map((img) => {
                    let delta = 0;
                    if (img.id === prevImageId && prevImageId !== imageId) delta -= 1;
                    if (img.id === imageId && prevImageId !== imageId) delta += 1;
                    return delta ? { ...img, usageCount: Math.max(0, (img.usageCount || 0) + delta) } : img;
                });
                return {
                    ...p,
                    tables: p.tables.map((t) => (t.id === tableId ? { ...t, imageId, imagePosition: position || null } : t)),
                    images: newImages,
                };
            });
            sync(api.put(`/tables/${tableId}`, { patch: { imageId, imagePosition: position || null } }));
        };

        return {
            refresh, refreshProducts, bumpProductsVersion,
            addSeries, updateSeries, removeSeries, renameSizeInSeries,
            addSizeToSeries, deleteSizeFromSeries, cloneSeries,
            addGroup, updateGroup, removeGroup, renameMeasureInGroup,
            addMeasureToGroup, deleteMeasureFromGroup, cloneGroup,
            addModel, updateModel, removeModel, setProductModel,
            addTable, updateTable, setTableActive, removeTable, linkProduct, unlinkProduct,
            setProductFit, setProductSizingExempt,
            setProductSizingImage,
            addProductsBulk, scrapeProductUrl, scrapeProductUrls,
            removeProduct, removeProductsBulk,
            fetchUnknownSkus, dismissUnknownSkus,
            updateFitLabels,
            uploadImage, renameImage, uploadImageVersion, rollbackImageVersion, removeImage, setTableImage,
        };
    }, [refresh, refreshProducts, bumpProductsVersion]);

    const value = useMemo(
        () => ({
            series: state.series, groups: state.groups, models: state.models,
            tables: state.tables, products: state.products, links: state.links,
            fitLabels: state.fitLabels,
            images: state.images,
            productsTotal: state.productsTotal,
            productsVersion: state.productsVersion,
            loaded: state.loaded,
            ...actions,
        }),
        [state, actions]
    );

    return <SizingContext.Provider value={value}>{children}</SizingContext.Provider>;
}

export const useSizing = () => {
    const ctx = useContext(SizingContext);
    if (!ctx) throw new Error("useSizing must be used within SizingProvider");
    return ctx;
};

/** A product has at most ONE model. Returns { model, size } or null.
 * Reads product-side FK (`p.modelId` + `p.wornSize`) — single source of truth
 * since we removed the reverse `model.products[]` array (2026-07-27).
 * Signature preserved for callsite compat; extended to accept products list. */
export const modelForProduct = (models, productId, products = null) => {
    // Support legacy (models, productId) callsites — return null if products not passed.
    if (!products) return null;
    const p = products.find((x) => x.id === productId);
    if (!p || !p.modelId) return null;
    const model = models.find((m) => m.id === p.modelId);
    if (!model) return null;
    return { model, size: p.wornSize || "" };
};

/** Table helpers over the links map (productId -> tableId). */
export const findTable = (tables, id) => tables.find((t) => t.id === id) || null;
export const countLinked = (links, tableId) =>
    Object.values(links).filter((tid) => tid === tableId).length;
export const linkedProductsOf = (links, tableId, products) =>
    products.filter((p) => links[p.id] === tableId);

/** ISO timestamp -> relative Turkish label. */
export const timeAgo = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return "şimdi";
    if (mins < 60) return `${mins} dk önce`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} sa önce`;
    const days = Math.floor(hours / 24);
    if (days === 1) return "dün";
    if (days < 30) return `${days} gün önce`;
    return d.toLocaleDateString("tr-TR");
};
